import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet';

import { AuthContext } from '../contexts/AuthContext';
import Loader from '../components/Loader';
import ButtonLoader from '../components/ButtonLoader';
import AseLogo from '../assets/ase-logo.png';

import workSessionService from '../services/workSessionService';
import { formatDateToSydneyTimeZone } from '../utils/dateUtils';

const formatDuration = (startTime) => {
  const diffMs = Date.now() - new Date(startTime).getTime();
  const totalMinutes = Math.floor(diffMs / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

const QRScanPage = () => {
  const { token: qrToken } = useParams();
  const { user, loading: authLoading } = useContext(AuthContext);

  const [projectName, setProjectName] = useState(null);
  const [tokenError, setTokenError] = useState(false);

  const [session, setSession] = useState(undefined);
  const [activeElsewhere, setActiveElsewhere] = useState(undefined);
  const [isLoadingSession, setIsLoadingSession] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successState, setSuccessState] = useState(null);

  useEffect(() => {
    workSessionService.resolveToken(qrToken)
      .then(({ projectName }) => setProjectName(projectName))
      .catch(() => setTokenError(true));
  }, [qrToken]);

  useEffect(() => {
    if (!user || user.role !== 'labourer') return;

    setIsLoadingSession(true);

    workSessionService.getSession(qrToken)
      .then(({ session, activeElsewhere }) => {
        setSession(session);
        setActiveElsewhere(activeElsewhere);
      })
      .catch(() => {
        setSession(null);
        setActiveElsewhere(null);
      })
      .finally(() => setIsLoadingSession(false));
  }, [qrToken, user]);

  const handleClockIn = async () => {
    try {
      setIsSubmitting(true);
      await workSessionService.clockIn(qrToken);
      setSuccessState('clock-in');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClockOut = async () => {
    try {
      setIsSubmitting(true);
      const entry = await workSessionService.clockOut(qrToken);
      setSuccessState({ type: 'clock-out', duration: formatDuration(entry.startTime) });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (tokenError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4">
        <img src={AseLogo} alt="ASE Group" className="h-[40px] mb-8" />
        <div className="bg-white rounded-lg border border-gray-200 shadow-md p-8 w-full max-w-sm text-center">
          <p className="text-2xl mb-2">&#128683;</p>
          <p className="font-bold text-lg mb-1">QR Code Invalid</p>
          <p className="text-gray-500 text-sm">This QR code is no longer valid. Please scan the current code posted on site.</p>
        </div>
      </div>
    );
  }

  if (authLoading || !projectName) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader />
      </div>
    );
  }

  if (!user) return null;

  if (user.role !== 'labourer') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4">
        <img src={AseLogo} alt="ASE Group" className="h-[40px] mb-8" />
        <div className="bg-white rounded-lg border border-gray-200 shadow-md p-8 w-full max-w-sm text-center">
          <p className="font-bold text-lg mb-1">Labourers Only</p>
          <p className="text-gray-500 text-sm">This page is only accessible to labourer accounts.</p>
        </div>
      </div>
    );
  }

  if (successState === 'clock-in') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4">
        <Helmet><title>Clocked In | ASE Group</title></Helmet>
        <img src={AseLogo} alt="ASE Group" className="h-[40px] mb-8" />
        <div className="bg-white rounded-lg border border-gray-200 shadow-md p-8 w-full max-w-sm text-center">
          <p className="text-4xl mb-4">&#9989;</p>
          <p className="font-bold text-xl mb-1">Clocked In</p>
          <p className="text-gray-600">{projectName}</p>
        </div>
      </div>
    );
  }

  if (successState?.type === 'clock-out') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4">
        <Helmet><title>Clocked Out | ASE Group</title></Helmet>
        <img src={AseLogo} alt="ASE Group" className="h-[40px] mb-8" />
        <div className="bg-white rounded-lg border border-gray-200 shadow-md p-8 w-full max-w-sm text-center">
          <p className="text-4xl mb-4">&#9989;</p>
          <p className="font-bold text-xl mb-1">Clocked Out</p>
          <p className="text-gray-600 mb-1">{projectName}</p>
          <p className="text-gray-500 text-sm">Duration: {successState.duration}</p>
        </div>
      </div>
    );
  }

  if (isLoadingSession || session === undefined || activeElsewhere === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader />
      </div>
    );
  }

  if (!session && activeElsewhere) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4">
        <Helmet><title>Already Clocked In | ASE Group</title></Helmet>
        <img src={AseLogo} alt="ASE Group" className="h-[40px] mb-8" />
        <div className="bg-white rounded-lg border border-gray-200 shadow-md p-8 w-full max-w-sm text-center">
          <p className="text-4xl mb-4">&#128683;</p>
          <p className="font-bold text-lg mb-2">{projectName}</p>
          <p className="text-gray-700 text-sm mb-1">
            You're already clocked in at <span className="font-semibold">{activeElsewhere.project?.name}</span>.
          </p>
          <p className="text-gray-500 text-sm">Clock out there before clocking in here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4">
      <Helmet>
        <title>{session ? 'Clock Out' : 'Clock In'} | ASE Group</title>
      </Helmet>

      <img src={AseLogo} alt="ASE Group" className="h-[40px] mb-8" />

      <div className="bg-white rounded-lg border border-gray-200 shadow-md p-8 w-full max-w-sm">
        <p className="font-bold text-lg mb-1 text-center">{projectName}</p>

        {!session && (
          <>
            <p className="text-gray-500 text-sm text-center mb-6">
              Tap the button below to clock in.
            </p>
            <button
              onClick={handleClockIn}
              disabled={isSubmitting}
              className="bg-ase-blue text-white font-semibold h-[56px] w-full rounded-md flex items-center justify-center text-lg"
            >
              {isSubmitting ? <ButtonLoader /> : 'Clock In'}
            </button>
          </>
        )}

        {session && (
          <>
            <p className="text-gray-500 text-sm text-center mb-1">
              You clocked in at {formatDateToSydneyTimeZone(session.startTime, 'HH:mm')}
            </p>
            <p className="text-gray-400 text-xs text-center mb-6">
              Duration so far: {formatDuration(session.startTime)}
            </p>
            <button
              onClick={handleClockOut}
              disabled={isSubmitting}
              className="bg-ase-blue text-white font-semibold h-[56px] w-full rounded-md flex items-center justify-center text-lg"
            >
              {isSubmitting ? <ButtonLoader /> : 'Clock Out'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default QRScanPage;
